import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AuthContext } from "../../../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import * as links from "../../../../utils/links";
import "./VisaoGeralCliente.css";

const dinheiro = (valor) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(valor || 0));

const formatarDataHora = (data) => {
  if (!data) return "--";
  const d = new Date(data);
  if (!Number.isFinite(d.getTime())) return "--";
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
};

const formatarHora = (data) => {
  if (!data) return "--:--";
  const d = new Date(data);
  if (!Number.isFinite(d.getTime())) return "--:--";
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(d);
};

const textoMensalidade = (mensalidade) => {
  const mapa = {
    ISENTO: "Isento",
    VENCIDO: "Vencida",
    VENCE_HOJE: "Vence hoje",
    VENCE_AMANHA: "Vence amanhã",
    VENCE_EM_7_DIAS: "Vence em até 7 dias",
    REGULAR: "Regular",
    SEM_VENCIMENTO: "Sem vencimento",
  };
  return mapa[mensalidade?.lembrete] || "--";
};

const identificacaoMaquina = (ultimoPagamento) => {
  const maquina = ultimoPagamento?.maquina;
  if (!maquina) return "Máquina não identificada";
  const descricao = String(maquina?.descricao || "").trim();
  const nome = String(maquina?.nome || "").trim();
  if (descricao && nome && descricao.toLowerCase() !== nome.toLowerCase()) {
    return `${descricao} · ${nome}`;
  }
  return descricao || nome || "Máquina não identificada";
};

const ehPix = (pagamento) => {
  const tipo = String(pagamento?.tipo ?? "").trim().toLowerCase();
  return tipo === "bank_transfer" || tipo === "11" || tipo === "pix";
};

const nomeMaquinaPagamento = (pagamento) => {
  const descricao = String(pagamento?.maquinaDescricao || "").trim();
  const nome = String(pagamento?.maquinaNome || "").trim();
  if (descricao && nome && descricao.toLowerCase() !== nome.toLowerCase()) {
    return `${descricao} · ${nome}`;
  }
  return descricao || nome || "Máquina";
};

export default function VisaoGeralCliente() {
  const { authInfo, setDataUser, setNotiMessage } = useContext(AuthContext);
  const token = authInfo?.dataUser?.token;
  const dataUser = authInfo?.dataUser;
  const navigate = useNavigate();
  const [carregando, setCarregando] = useState(true);
  const [resumo, setResumo] = useState(null);
  const [extra, setExtra] = useState(null);
  const [ultimosPix, setUltimosPix] = useState([]);

  const headers = useMemo(() => ({
    "x-access-token": token,
    "content-type": "application/json",
  }), [token]);

  const carregar = async () => {
    setCarregando(true);
    const servidor = process.env.REACT_APP_SERVIDOR;
    const resultados = await Promise.allSettled([
      axios.get(`${servidor}/cliente-2026/visao-geral`, { headers }),
      axios.get(`${servidor}/cliente-2027/resumo-extra`, { headers }),
      axios.get(`${servidor}/payments-client?filtro=mesatual`, { headers }),
    ]);

    const erroSessao = resultados.some(
      (r) => r.status === "rejected" && [401, 403].includes(r.reason?.response?.status)
    );
    if (erroSessao) {
      setNotiMessage({ type: "error", message: "A sua sessão expirou. Faça login novamente." });
      setDataUser(null);
      navigate(links.SIGNIN);
      return;
    }

    if (resultados[0].status === "fulfilled") setResumo(resultados[0].value.data || null);
    if (resultados[1].status === "fulfilled") setExtra(resultados[1].value.data || null);
    if (resultados[2].status === "fulfilled") {
      const pagamentos = Array.isArray(resultados[2].value?.data?.pagamentosRecentes)
        ? resultados[2].value.data.pagamentosRecentes
        : [];

      const listaPix = pagamentos
        .filter(ehPix)
        .slice()
        .sort((a, b) => new Date(b?.data || 0).getTime() - new Date(a?.data || 0).getTime())
        .slice(0, 10);

      setUltimosPix(listaPix);
    } else {
      setUltimosPix([]);
    }
    setCarregando(false);
  };

  useEffect(() => {
    carregar();
    const timer = setInterval(carregar, 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tituloEmpresa = resumo?.cliente?.nome || dataUser?.empresa || dataUser?.name || "Minha empresa";
  const maquinas = Number(resumo?.maquinas || 0);
  const online = Number(resumo?.online || 0);
  const offline = Number(resumo?.offline || 0);
  const sinalFraco = Number(resumo?.sinalRuim || 0);

  return (
    <div className="cliente-overview">
      <div className="cliente-overview-topo">
        <div>
          <span className="cliente-overview-kicker">VISÃO GERAL</span>
          <h1>{tituloEmpresa}</h1>
          <p>Resumo operacional e rendimento em PIX da sua empresa.</p>
        </div>
        <button type="button" className="cliente-overview-atualizar" onClick={carregar} disabled={carregando}>
          <i className="bi bi-arrow-clockwise" /> {carregando ? "Atualizando..." : "Atualizar"}
        </button>
      </div>

      <div className="cliente-overview-grid principal">
        <div className="cliente-overview-card destaque-azul">
          <span>PIX DE HOJE</span>
          <strong>{dinheiro(resumo?.pixHoje || 0)}</strong>
          <small>{resumo?.periodoHoje || "00:00 às 23:59"} · todas as máquinas</small>
        </div>
        <div className="cliente-overview-card destaque-ciano">
          <span>PIX DO MÊS</span>
          <strong>{dinheiro(resumo?.pixMes || 0)}</strong>
          <small>{resumo?.periodoMes || "dia 01 até o último dia do mês"} · zera no próximo mês</small>
        </div>
        <div className="cliente-overview-card destaque-verde">
          <span>ONLINE</span>
          <strong>{online}</strong>
          <small>de {maquinas} máquina(s)</small>
        </div>
        <div className="cliente-overview-card destaque-vermelho">
          <span>OFFLINE</span>
          <strong>{offline}</strong>
          <small>sem comunicação</small>
        </div>
      </div>

      <div className="cliente-overview-grid secundario">
        <div className="cliente-overview-card compacto">
          <span>MÁQUINAS</span>
          <strong>{maquinas}</strong>
          <small>total desta empresa</small>
        </div>
        <div className="cliente-overview-card compacto destaque-amarelo">
          <span>SINAL FRACO</span>
          <strong>{sinalFraco}</strong>
          <small>online com até 30%</small>
        </div>
        <div className="cliente-overview-card compacto">
          <span>MENSALIDADE</span>
          <strong className="texto-status">{textoMensalidade(extra?.mensalidade)}</strong>
          <small>{extra?.mensalidade?.dataVencimento ? `vencimento ${new Date(extra.mensalidade.dataVencimento).toLocaleDateString("pt-BR")}` : "situação da conta"}</small>
        </div>
        <div className="cliente-overview-card compacto">
          <span>ÚLTIMO PAGAMENTO</span>
          <strong className="texto-status">{extra?.ultimoPagamento ? dinheiro(extra.ultimoPagamento.valor) : "--"}</strong>
          <small>{formatarDataHora(extra?.ultimoPagamento?.data)}</small>
          {extra?.ultimoPagamento && (
            <small className="ultimo-pagamento-maquina">Máquina: {identificacaoMaquina(extra.ultimoPagamento)}</small>
          )}
        </div>
      </div>

      <section className="cliente-overview-ranking">
        <div className="cliente-overview-section-title">
          <div>
            <span>ÚLTIMOS PIX RECEBIDOS</span>
            <h2>10 últimos PIX recebidos</h2>
          </div>
          <small>Mais recente em cima · atualiza automaticamente</small>
        </div>
        {ultimosPix.length ? (
          <div className="cliente-overview-ranking-lista">
            {ultimosPix.map((item, index) => (
              <div className="cliente-overview-ranking-item" key={item?.id || item?.mercadoPagoId || `${item?.data}-${index}`}>
                <div className="ranking-posicao">{index + 1}</div>
                <div className="ranking-nome">
                  <strong>{nomeMaquinaPagamento(item)}</strong>
                  <small>PIX recebido às {formatarHora(item?.data)}</small>
                </div>
                <strong className="ranking-valor">{dinheiro(item?.valor)}</strong>
              </div>
            ))}
          </div>
        ) : (
          <div className="cliente-overview-vazio">Ainda não há PIX recebido para mostrar.</div>
        )}
      </section>
    </div>
  );
}
